import React from 'react';
import moment from 'moment';
import type { PriceChangeSubmission } from '@app/model/pricing-automation/PriceChangeSubmission';
import { placePriceChangeDays, PriceChangeDay, summarizePriceChangeDay } from './PriceChangeTimeline';

export const syncPriceChangeMarkerColor = (chart: any) => {
	const hotel = chart.get('main-hotel');
	const markers = chart.get('price-changes');
	if (!hotel || !markers || markers.options.color === hotel.color) return;
	markers.update({ color: hotel.color, marker: { fillColor: hotel.color, lineColor: '#ffffff', lineWidth: 2, radius: 7 } }, false);
	chart.redraw();
};

export const priceChangeMarkerSeries = (pointDates: string[], hotelPoints: any[], submissions: PriceChangeSubmission[]) => {
	const days = placePriceChangeDays(pointDates, submissions);
	const pointIndices = [...new Set(days.map(day => day.pointIndex))];
	return {
		days,
		series: pointIndices.length ? {
			id: 'price-changes', type: 'scatter', name: 'Price changes', showInLegend: true, animation: false,
			zIndex: 10, lineWidth: 0,
			marker: { symbol: 'circle', radius: 7, lineWidth: 2, lineColor: '#ffffff',
				states: { hover: { enabled: false } } },
			data: pointIndices.map(pointIndex => ({ x: pointIndex, y: hotelPoints[pointIndex]?.y ?? null })),
		} : null,
	};
};

type TooltipContext = { x?: number; point?: { x: number }; points?: { point: { x: number } }[] };

// The x index of the hovered day: a shared tooltip gives points, a dot hover gives the dot itself.
export const tooltipPointIndex = (context: unknown): number => {
	const { x, point, points } = context as TooltipContext;
	return points?.[0]?.point.x ?? point?.x ?? x ?? 0;
};

const price = (amount: number) => amount.toFixed(2);

// The price-change part of the chart tooltip for one x index. It renders nothing when the day has no change.
export const PriceChangeTooltipSection: React.FC<{ days: PriceChangeDay[]; pointIndex: number }> = ({ days, pointIndex }) => {
	const selected = days.filter(day => day.pointIndex === pointIndex);
	if (selected.length === 0) return null;
	return <div className="mt-1 border-t border-gray-300 pt-2 text-xs text-gray-700" style={{ minWidth: 260, whiteSpace: 'normal' }}>
		{selected.map(day => {
			const { rows, resendCount } = summarizePriceChangeDay(day);
			return <div key={day.date} className="mb-2 last:mb-0">
				<div className="font-semibold text-gray-900">Price change · {moment(day.date).format('DD/MM/YYYY')}</div>
				<div className="text-gray-500">{day.submissions.length} {day.submissions.length === 1 ? 'submission' : 'submissions'} · {day.submissions.map(s => `${moment.utc(s.finishDate).local().format('HH:mm')} ${s.profileName ?? 'Unknown user'}`).join(', ')}</div>
				{rows.map(room => {
					const percentage = room.previous ? ((room.latest - room.previous) / room.previous) * 100 : null;
					return <div key={room.roomId} className="mt-1 flex justify-between gap-3 whitespace-nowrap"><span>{room.name}: {room.previous == null ? '—' : price(room.previous)} → {price(room.latest)}</span>
						{percentage !== null && <span className={percentage >= 0 ? 'text-green-500' : 'text-red-500'}>{percentage >= 0 ? '▲' : '▼'} {Math.abs(percentage).toFixed(1)}%</span>}</div>;
				})}
				{resendCount > 0 && <div className="mt-1 text-gray-500">{resendCount} {resendCount === 1 ? 'room' : 'rooms'} re-sent with no change</div>}
			</div>;
		})}
	</div>;
};
